import { subDays } from "date-fns"
import { takeWhile } from "lodash-es"

import { MONITORING_HISTORY_START } from "@/constants/terminal/monitoring-history"
import type {
  TerminalHistoryAssessment,
  TerminalPreviousAssessment,
  TerminalScoreChange,
  TerminalScoreMovement,
} from "@/lib/terminal/file-view"
import {
  DIGEST_SCORE_THRESHOLD,
  digestMove,
  isDigestMove,
} from "@/terminal/digest/file-change"

// How an Assessment moved from the one before it, by the Digest's own rule,
// so every movement mark agrees with the email: null without an earlier
// Assessment or when the move does not count.
export const scoreMovement = (
  current: Pick<TerminalHistoryAssessment, "band" | "score" | "drivers">,
  previous: TerminalPreviousAssessment | null
): TerminalScoreMovement | null => {
  if (!previous) return null
  const move = digestMove({ before: previous, after: current })
  return isDigestMove(move, DIGEST_SCORE_THRESHOLD)
    ? {
        ...move,
        previous: { band: previous.band, score: previous.score },
        drivers: current.drivers,
      }
    : null
}

// A Company's counted score changes since monitoring history began, or since
// `since` when that is later, newest first: each Assessment of its history
// (newest first) that moved from its previous.
export const scoreChanges = (
  history: TerminalHistoryAssessment[],
  since = MONITORING_HISTORY_START
) => {
  const start =
    since > MONITORING_HISTORY_START ? since : MONITORING_HISTORY_START
  return takeWhile(
    history,
    (assessment) => assessment.assessedAt >= start
  ).flatMap((assessment) => {
    const movement = scoreMovement(assessment, assessment.previous)
    return movement ? [{ ...assessment, movement }] : []
  })
}

// The move every buyer screen shows for a monitored Company: its latest
// counted move, which lasts until the next. A snapshot shows none.
export const fileScoreMovement = (
  history: TerminalHistoryAssessment[],
  isMonitoring: boolean
) =>
  (isMonitoring ? scoreChanges(history.slice(0, 1))[0]?.movement : null) ?? null

// How far back Home's Activity lists a Company's moves.
const RECENT_CHANGE_DAYS = 90

// A monitored Company's counted moves in the 90 days before `now`, newest
// first. Home lists each as its own row.
export const recentScoreChanges = (
  history: TerminalHistoryAssessment[],
  isMonitoring: boolean,
  now: Date
): TerminalScoreChange[] =>
  isMonitoring
    ? scoreChanges(history, subDays(now, RECENT_CHANGE_DAYS).toISOString()).map(
        ({ band, score, assessedAt, movement }) => ({
          band,
          score,
          assessedAt,
          movement,
        })
      )
    : []
